import { useMutation, useQueryClient } from 'react-query';
import fundingRequests from 'services/api/fundingRequests';
import useTranslation from 'utils/hooks/useTranslation';
import notify from 'utils/notificationUtils';
import useAmplitude from 'utils/hooks/useAmplitude';
import { ddLogWithContext } from 'utils/errorLoggerUtil';
import { getAnalyticsErrorReason, toAnalyticsCurrency, toMajorUnits } from 'utils/walletUtil';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import { walletQueryKeys, wireTransferQueryKeys } from './queryKeys';

const { getWireTransfersQuery } = wireTransferQueryKeys;
const { getWalletQuery } = walletQueryKeys;
const { outcome: OUTCOMES } = AMPLITUDE_WALLET_PROPERTY_VALUES;

// `amountMinor` and `currency` ride along on the mutation variables for analytics only; the API
// call takes just the id, deposit reference and notes. The note text itself is never sent.
const getDecisionEventProperties = (variables, outcome) => ({
  requestId: variables?.id,
  amount: toMajorUnits(variables?.amountMinor),
  currency: toAnalyticsCurrency(variables?.currency),
  outcome,
  hasNote: !!variables?.notes?.trim(),
});

const getDecisionFailedEventProperties = (error, variables, outcome) => ({
  requestId: variables?.id,
  errorReason: getAnalyticsErrorReason(error),
  attempted_outcome: outcome,
});

// A decision moves the request out of "pending", which changes the table, the Pending tab
// count and the left-menu badge — all of them read through getWireTransfersQuery. Invalidate
// the whole key rather than predicate-matching individual status/page/filter combinations:
// the narrower form bought nothing and gave the refetch a way to silently match nothing.
const invalidateWireTransferQueries = (queryClient, context) => {
  const matched = queryClient.getQueryCache().findAll(getWireTransfersQuery).length;

  // A decision with nothing to invalidate means the reviewer is looking at a list this
  // refetch will never reach — the approved request just stays on screen under Pending.
  if (!matched) {
    ddLogWithContext({
      type: 'warn',
      message: 'Funding request decision matched no cached wire-transfer query to refetch',
      context,
    });
  }

  queryClient.invalidateQueries(getWireTransfersQuery, { exact: false });
  // Approving credits the wallet and declining releases the pending amount, so the
  // wallet read is stale either way — re-read it for whoever is showing a dashboard.
  queryClient.invalidateQueries(getWalletQuery, { exact: false });
};

// react-query awaits onSuccess in the same promise chain as mutationFn
// (react-query/lib/core/mutation.js), so anything that throws in here is reported to the
// caller as a failed mutation — the reviewer would see "Something went wrong approving
// this funding request" after the server returned 200, with the modal still open.
// A toast, an analytics call or a cache invalidation must never be able to do that.
//
// Each effect is isolated from the others as well: the cache invalidation is what makes
// the approved request leave the Pending tab and the badges drop, so a throwing toast or
// analytics call must not be able to skip it (or vice versa). The name identifies the
// culprit in Datadog, since the reviewer sees nothing.
const runSideEffects = (effects, context) =>
  effects.forEach(([name, effect]) => {
    try {
      effect();
    } catch (error) {
      ddLogWithContext({
        type: 'error',
        message: 'Funding request decision succeeded but a post-success side effect threw',
        context: { ...context, effect: name, error: error?.message },
      });
    }
  });

export const useApproveFundingRequest = () => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, depositReference, notes }) => {
      const result = await fundingRequests.approve({ id, depositReference, notes });
      return result?.data;
    },
    onSuccess: (_data, variables) => {
      runSideEffects(
        [
          [
            'invalidate',
            () =>
              invalidateWireTransferQueries(queryClient, {
                action: 'approve',
                wireTransferId: variables?.id,
              }),
          ],
          [
            'notify',
            () =>
              notify({
                type: 'success',
                message: translateText(
                  'corporateAccounts.fundingRequests.reviewModal.approveSuccess',
                ),
              }),
          ],
          [
            'amplitude',
            () =>
              sendAmplitudeEvent(
                AMPLITUDE_EVENT_TYPES.fundingRequestApproveSuccessful,
                getDecisionEventProperties(variables, OUTCOMES.approved),
              ),
          ],
        ],
        { action: 'approve', wireTransferId: variables?.id },
      );
    },
    onError: (error, variables) => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.fundingRequests.reviewModal.approveError'),
      });
      sendAmplitudeEvent(
        AMPLITUDE_EVENT_TYPES.fundingRequestApproveFailed,
        getDecisionFailedEventProperties(error, variables, OUTCOMES.approved),
      );
    },
  });
};

export const useDeclineFundingRequest = () => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, depositReference, notes }) => {
      const result = await fundingRequests.decline({ id, depositReference, notes });
      return result?.data;
    },
    onSuccess: (_data, variables) => {
      runSideEffects(
        [
          [
            'invalidate',
            () =>
              invalidateWireTransferQueries(queryClient, {
                action: 'decline',
                wireTransferId: variables?.id,
              }),
          ],
          [
            'notify',
            () =>
              notify({
                type: 'success',
                message: translateText(
                  'corporateAccounts.fundingRequests.reviewModal.declineSuccess',
                ),
              }),
          ],
          [
            'amplitude',
            () =>
              sendAmplitudeEvent(
                AMPLITUDE_EVENT_TYPES.fundingRequestDeclineSuccessful,
                getDecisionEventProperties(variables, OUTCOMES.rejected),
              ),
          ],
        ],
        { action: 'decline', wireTransferId: variables?.id },
      );
    },
    onError: (error, variables) => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.fundingRequests.reviewModal.declineError'),
      });
      sendAmplitudeEvent(
        AMPLITUDE_EVENT_TYPES.fundingRequestDeclineFailed,
        getDecisionFailedEventProperties(error, variables, OUTCOMES.rejected),
      );
    },
  });
};

export default { useApproveFundingRequest, useDeclineFundingRequest };
